import { useForm, useList } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { DatePicker } from '@/components/ui/date-picker';
import { ContentTagsField } from '@/components/shared';
import { FormField, FormHeader, FormSpacer, FormStickyActions } from '@/components/forms';
import { FileImageUpload } from '@/components/upload/FileImageUpload';
import { LanguageTabs } from '@/components/universal';
import { SegmentBuilder, AudienceUsersTable } from '@/components/audience-segment';
import {
  Settings,
  Image as ImageIcon,
  Monitor,
  Tablet,
  Smartphone,
  Link as LinkIcon,
  Users,
  ChevronsUpDown,
  Check,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SegmentDefinition } from '@masters-club/validation';
import {
  DEVICE_SLOTS,
  LINK_TARGET_OPTIONS,
  type Announcement,
  type AnnouncementFormData,
  type AnnouncementImageInput,
  type LinkTargetType,
} from './types';

const EMPTY_SEGMENT: SegmentDefinition = { groupsOp: 'AND', groups: [] };

interface LanguageRecord {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
}

interface LanguageTabItem {
  code: string;
  label: string;
  flagEmoji?: string;
}

interface ImageSlot {
  aspectRatio: string;
  deviceLabel: string;
  languageCode: string | null;
  imageId: number | null;
  imageUrl: string;
}

const DEVICE_ICONS = {
  Desktop: Monitor,
  Tablet: Tablet,
  Mobile: Smartphone,
} as const;

function buildImageSlots(
  existingImages: AnnouncementImageInput[],
  languages: LanguageTabItem[] | undefined,
  localizationEnabled: boolean,
): ImageSlot[] {
  const slots: ImageSlot[] = [];

  if (localizationEnabled && languages && languages.length > 0) {
    for (const lang of languages) {
      for (const device of DEVICE_SLOTS) {
        const existing = existingImages.find(
          (img) => img.aspectRatio === device.aspectRatio && img.languageCode === lang.code,
        );
        slots.push({
          aspectRatio: device.aspectRatio,
          deviceLabel: device.label,
          languageCode: lang.code,
          imageId: existing?.imageId ?? null,
          imageUrl: existing?.imageUrl ?? '',
        });
      }
    }
  } else {
    for (const device of DEVICE_SLOTS) {
      const existing = existingImages.find(
        (img) => img.aspectRatio === device.aspectRatio && !img.languageCode,
      );
      slots.push({
        aspectRatio: device.aspectRatio,
        deviceLabel: device.label,
        languageCode: null,
        imageId: existing?.imageId ?? null,
        imageUrl: existing?.imageUrl ?? '',
      });
    }
  }

  return slots;
}

function slotsToImageInputs(slots: ImageSlot[]): AnnouncementImageInput[] {
  return slots
    .filter((s): s is ImageSlot & { imageId: number } => s.imageId !== null)
    .map((s, i) => ({
      imageId: s.imageId,
      imageUrl: s.imageUrl,
      aspectRatio: s.aspectRatio,
      languageCode: s.languageCode,
      type: 'background' as const,
      order: i,
    }));
}

// ── Link target (internal-сущность или произвольный URL) ──────────────
function parseLinkTarget(linkUrl: string | null): { targetType: LinkTargetType; entityId: string; customUrl: string } {
  if (!linkUrl) {
    return { targetType: 'custom', entityId: '', customUrl: '' };
  }
  for (const opt of LINK_TARGET_OPTIONS) {
    if (opt.value === 'custom') { continue; }
    const prefix = `/${opt.value}/`;
    if (linkUrl.startsWith(prefix)) {
      return { targetType: opt.value, entityId: linkUrl.slice(prefix.length), customUrl: '' };
    }
  }
  return { targetType: 'custom', entityId: '', customUrl: linkUrl };
}

function buildLinkUrl(targetType: LinkTargetType, entityId: string, customUrl: string): string | null {
  if (targetType === 'custom') {
    return customUrl || null;
  }
  if (!entityId) { return null; }
  return `/${targetType}/${entityId}`;
}

interface LinkableEntity {
  id: number | string;
  translations?: Array<{ title?: string; languageCode?: string }> | Record<string, { name?: string }>;
  code?: string;
  slug?: string;
  name?: string;
  title?: string;
  content?: string;
}

function getEntityDisplayName(resource: string, item: LinkableEntity): string {
  const { translations } = item;
  switch (resource) {
    case 'courses': {
      if (Array.isArray(translations)) {
        const t = translations.find((tr) => tr.languageCode === 'ru') ?? translations[0];
        if (t?.title) { return t.title; }
      }
      return item.slug ?? item.title ?? `#${item.id}`;
    }
    case 'subscription-plans':
    case 'channel-passes': {
      if (translations && !Array.isArray(translations)) {
        const t = translations.ru ?? Object.values(translations)[0];
        if (t?.name) { return t.name; }
      }
      return item.code ?? item.name ?? `#${item.id}`;
    }
    case 'posts': {
      const content = String(item.content ?? '').replaceAll(/<[^>]*>/g, '').slice(0, 80);
      return content ? `#${item.id} ${content}` : `Пост #${item.id}`;
    }
    default:
      return String(item.name ?? item.title ?? `#${item.id}`);
  }
}

function EntitySelector({ resource, value, onChange }: { resource: string; value: string; onChange: (id: string) => void }) {
  const [open, setOpen] = useState(false);

  const { result } = useList<LinkableEntity>({
    resource,
    pagination: { pageSize: 100, mode: 'server' },
  });

  const items = result?.data;
  const label = LINK_TARGET_OPTIONS.find((o) => o.value === resource)?.label ?? 'Сущность';

  const options = useMemo(
    () => (items ?? []).map((item) => ({
      value: String(item.id),
      label: getEntityDisplayName(resource, item),
    })),
    [items, resource],
  );

  const placeholder = `Выберите ${label.toLowerCase()}`;
  const displayValue = options.find((o) => o.value === value)?.label ?? placeholder;

  return (
    <FormField label={label} name="linkEntityId">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between font-normal h-auto min-h-10">
            <span className="truncate text-left">{displayValue}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" sideOffset={4}>
          <Command>
            <CommandInput placeholder="Поиск..." />
            <CommandList>
              <CommandEmpty>Ничего не найдено</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="__none__"
                  onSelect={() => {
                    onChange('');
                    setOpen(false);
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                  Не выбрано
                </CommandItem>
                {options.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn('mr-2 h-4 w-4', value === option.value ? 'opacity-100' : 'opacity-0')} />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </FormField>
  );
}

export function AnnouncementsForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { formLoading, onFinish, query } = useForm<Announcement>({
    resource: 'announcements',
    action: isEdit ? 'edit' : 'create',
    id,
    redirect: 'list',
  });

  const { result: languagesResult } = useList<LanguageRecord>({
    resource: 'languages',
  });

  const languages: LanguageTabItem[] | undefined = languagesResult?.data?.map((l) => ({
    code: l.code,
    label: l.nativeName || l.name,
    flagEmoji: l.flagEmoji || l.flag || undefined,
  }));

  const announcement = query?.data?.data;
  const formKey = isEdit ? `edit-${announcement?.id ?? 'loading'}` : 'create';

  return (
    <AnnouncementsFormFields
      key={formKey}
      isEdit={isEdit}
      formLoading={formLoading}
      announcement={announcement}
      order={announcement?.order ?? 0}
      onFinish={async (v) => {
        await onFinish(v);
      }}
      onBack={() => navigate(-1)}
      languages={languages}
    />
  );
}

interface AnnouncementsFormFieldsProps {
  isEdit: boolean;
  formLoading: boolean;
  announcement?: Announcement;
  order: number;
  onFinish: (values: AnnouncementFormData) => Promise<void>;
  onBack: () => void;
  languages?: LanguageTabItem[];
}

function AnnouncementsFormFields({
  isEdit,
  formLoading,
  announcement,
  order,
  onFinish,
  onBack,
  languages,
}: AnnouncementsFormFieldsProps) {
  const parsedLink = parseLinkTarget(announcement?.linkUrl ?? null);
  const [linkTargetType, setLinkTargetType] = useState<LinkTargetType>(parsedLink.targetType);
  const [linkEntityId, setLinkEntityId] = useState(parsedLink.entityId);
  const [linkCustomUrl, setLinkCustomUrl] = useState(parsedLink.customUrl);

  const [startDate, setStartDate] = useState<Date | null>(
    announcement?.startDate ? new Date(announcement.startDate) : null,
  );
  const [endDate, setEndDate] = useState<Date | null>(
    announcement?.endDate ? new Date(announcement.endDate) : null,
  );
  const [isActive, setIsActive] = useState(announcement?.isActive ?? true);

  const [audienceSegment, setAudienceSegment] = useState<SegmentDefinition>(
    announcement?.audienceSegment ?? EMPTY_SEGMENT,
  );

  const [localizationEnabled, setLocalizationEnabled] = useState(() => {
    const imgs = announcement?.images ?? [];
    return imgs.some((img) => img.languageCode !== null);
  });
  const [activeLanguage, setActiveLanguage] = useState(languages?.[0]?.code ?? 'ru');

  const existingImages: AnnouncementImageInput[] = (announcement?.images ?? []).map((img) => ({
    imageId: img.imageId,
    imageUrl: img.imageUrl,
    aspectRatio: img.aspectRatio,
    languageCode: img.languageCode,
    type: (img.type ?? 'background') as 'background',
    order: img.order,
  }));

  const [imageSlots, setImageSlots] = useState<ImageSlot[]>(() =>
    buildImageSlots(existingImages, languages, localizationEnabled),
  );

  const updateSlotImage = (index: number, fileRecord: { id: number; url: string } | null) => {
    setImageSlots((prev) =>
      prev.map((s, i) =>
        i === index
          ? { ...s, imageId: fileRecord?.id ?? null, imageUrl: fileRecord?.url ?? '' }
          : s,
      ),
    );
  };

  const handleLocalizationChange = (enabled: boolean) => {
    setLocalizationEnabled(enabled);
    const currentInputs = slotsToImageInputs(imageSlots);
    setImageSlots(buildImageSlots(currentInputs, languages, enabled));
  };

  const hasAnyImage = imageSlots.some((s) => s.imageId !== null);

  const hasAudienceSegment = audienceSegment.groups.length > 0;

  const handleSubmit = async () => {
    const formData: AnnouncementFormData = {
      images: slotsToImageInputs(imageSlots),
      linkUrl: buildLinkUrl(linkTargetType, linkEntityId, linkCustomUrl),
      // Пустой сегмент (нет групп) → null: показывать всем.
      audienceSegment: hasAudienceSegment ? audienceSegment : null,
      order,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
      isActive,
    };
    await onFinish(formData);
  };

  const visibleSlots = localizationEnabled
    ? imageSlots
        .map((slot, originalIndex) => ({ slot, originalIndex }))
        .filter(({ slot }) => slot.languageCode === activeLanguage)
    : imageSlots.map((slot, originalIndex) => ({ slot, originalIndex }));

  return (
    <div className="space-y-6">
      <FormHeader
        title={isEdit ? 'Редактировать объявление' : 'Создать объявление'}
        onBack={onBack}
        sticky
      />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left — Images / Link / Audience */}
        <div className="xl:col-span-2 space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  Изображения
                </span>
                {languages && languages.length > 1 && (
                  <div className="flex items-center gap-2">
                    <Label htmlFor="localization-toggle" className="text-xs font-normal text-muted-foreground cursor-pointer">
                      Локализация
                    </Label>
                    <Select
                      value={localizationEnabled ? 'on' : 'off'}
                      onValueChange={(v) => handleLocalizationChange(v === 'on')}
                    >
                      <SelectTrigger id="localization-toggle" className="w-[100px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="off">Выкл</SelectItem>
                        <SelectItem value="on">Вкл</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {localizationEnabled && languages && languages.length > 0 && (
                <LanguageTabs
                  languages={languages}
                  activeLanguage={activeLanguage}
                  onLanguageChange={setActiveLanguage}
                />
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {visibleSlots.map(({ slot, originalIndex }) => {
                  const DeviceIcon = DEVICE_ICONS[slot.deviceLabel as keyof typeof DEVICE_ICONS] ?? Monitor;
                  const deviceMeta = DEVICE_SLOTS.find((d) => d.label === slot.deviceLabel);

                  return (
                    <div key={originalIndex} className="space-y-2">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <DeviceIcon className="h-3.5 w-3.5" />
                        <span className="font-medium">{slot.deviceLabel}</span>
                        <span>({deviceMeta?.description})</span>
                      </div>
                      <FileImageUpload
                        value={slot.imageId ? { id: slot.imageId, url: slot.imageUrl } : null}
                        onChange={(fileRecord) => updateSlotImage(originalIndex, fileRecord)}
                        placeholder={`${slot.aspectRatio}`}
                      />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <LinkIcon className="h-4 w-4" />
                Ссылка
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField label="Тип ссылки" name="linkTargetType">
                <Select
                  value={linkTargetType}
                  onValueChange={(v) => {
                    setLinkTargetType(v as LinkTargetType);
                    setLinkEntityId('');
                    setLinkCustomUrl('');
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LINK_TARGET_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              {linkTargetType === 'custom' ? (
                <FormField label="URL" name="linkCustomUrl">
                  <Input
                    value={linkCustomUrl}
                    onChange={(e) => setLinkCustomUrl(e.target.value)}
                    placeholder="https://..."
                  />
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Внутренняя ссылка (относительный путь / свой домен) откроется внутри платформы, внешняя — в новой вкладке.
                  </p>
                </FormField>
              ) : (
                <EntitySelector
                  resource={linkTargetType}
                  value={linkEntityId}
                  onChange={setLinkEntityId}
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Целевая аудитория
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-4">
                Оставьте пустым — объявление будет показано всем пользователям (с учётом тегов и языка).
              </p>
              <SegmentBuilder
                value={audienceSegment}
                onChange={setAudienceSegment}
                disabled={formLoading}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Кому покажется
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-4">
                {hasAudienceSegment
                  ? 'Предпросмотр пользователей, удовлетворяющих условиям сегмента. Только они увидят объявление (с учётом тегов и языка).'
                  : 'Без условий объявление видят все пользователи. Список ниже — все пользователи системы.'}
              </p>
              <AudienceUsersTable
                previewUrl="/audience/preview"
                definition={audienceSegment}
                disabled={formLoading}
                showMatchColumn={hasAudienceSegment}
                matchColumnHeader="Увидит объявление"
                matchTrueLabel="Увидит"
                matchFalseLabel="Не увидит"
              />
            </CardContent>
          </Card>
        </div>

        {/* Right — Settings / Tags */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Настройки
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField label="Начало показа" name="startDate">
                <DatePicker
                  value={startDate}
                  onChange={setStartDate}
                  placeholder="Без ограничения"
                />
              </FormField>

              <FormField label="Конец показа" name="endDate">
                <DatePicker
                  value={endDate}
                  onChange={setEndDate}
                  placeholder="Без ограничения"
                />
              </FormField>

              <FormField label="Статус" name="isActive">
                <Select
                  value={isActive ? 'active' : 'inactive'}
                  onValueChange={(v) => setIsActive(v === 'active')}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Активно</SelectItem>
                    <SelectItem value="inactive">Неактивно</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </CardContent>
          </Card>

          {isEdit && announcement?.id ? (
            <Card>
              <CardContent className="pt-6">
                <ContentTagsField entityType="PROMO_ANNOUNCEMENT" entityId={announcement.id} />
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      <FormSpacer />

      <FormStickyActions>
        <Button onClick={handleSubmit} disabled={formLoading || !hasAnyImage}>
          {isEdit ? 'Сохранить' : 'Создать'}
        </Button>
        <Button variant="outline" onClick={onBack}>
          Отмена
        </Button>
      </FormStickyActions>
    </div>
  );
}
